import React, { useEffect, useState, useRef, useMemo } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { selectUser } from '../../features/userSlice';
import styles from './UnloadingEntry.module.css';
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Truck, Building2, IdCard, Package, Scale } from "lucide-react";
import { usePopup } from '../../utils/PopupContext';
import InAppCamera from "../../components/InAppCamera";
import FloatingActions from '../../components/FloatingActions';

const NewUnloadingEntryQCScreen = () => {

  const [placeOptions, setPlaceOptions] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [yardPlaceOptions, setYardPlaceOptions] = useState([]);
  const [yardTransferPlace, setYardTransferPlace] = useState(null);
  const [truckList, setTruckList] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [unloadingMethod, setUnloadingMethod] = useState('Both');
  const [remarks, setRemarks] = useState('');

  //const [toastMessage, setToastMessage] = useState('');

  const [weightType] = useState('P');
  const [entityCode] = useState('SE');
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const user = useSelector(selectUser);
  const emp_id = user?.emp_id;
  const token = user?.token;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  //const [popup, setPopup] = useState({ open: false, title: '', message: '' });
  //const showPopup = (message, title = 'Notice') => setPopup({ open: true, title, message });
  //const closePopup = () => setPopup({ open: false, title: '', message: '' });
  const { showPopup } = usePopup();

  const SEPL_BLUE = "#1F6AA5";
  const SEPL_ORANGE = "#F39C12";

  // ---------------- Two-phase workflow + photos + GPS + geofence + existing photos ----------------
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);

  const [afterPhoto, setAfterPhoto] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);

  // ---- photo metadata (new table fields; frontend computed) ----
  const [photoMeta, setPhotoMeta] = useState({
    before: { size_bytes: null, sha256: null },
    after: { size_bytes: null, sha256: null },
  });

  const [gps, setGps] = useState({ lat: null, lng: null, accuracy: null, error: null });
  const [geoLoading, setGeoLoading] = useState(false);
  const isAutoSettingPlace = useRef(false); // prevent loops when auto-selecting LOV

  // geofence stations: [{ id, name, lat, lng, radiusMeters }]
  const [stations, setStations] = useState([]);
  const [autoStation, setAutoStation] = useState(null);
  const [selectionSource, setSelectionSource] = useState(null); // 'auto' | 'manual' | null

  const [qcRequired, setQcRequired] = useState('N');  // default
  const [qcLocked, setQcLocked] = useState(false);    // when true, user cannot change to N

  // ---- QC txn parameters (new table fields) ----
  const [qcTestCode, setQcTestCode] = useState('');
  const [qcTestValue, setQcTestValue] = useState('');
  const [qcParams, setQcParams] = useState([]);
  const [qcLoading, setQcLoading] = useState(false);

  const [qcDedAmt, setQcDedAmt] = useState('');
  const [qcTestStatus, setQcTestStatus] = useState('P');

  // inside component:
  const ua = (navigator.userAgent || "").toLowerCase();
  const isXiaomiLike = /miui|hyperos|redmi|xiaomi/.test(ua);
  const [useInAppCam, setUseInAppCam] = useState(isXiaomiLike); // default true for MIUI
  const [showInAppCam, setShowInAppCam] = useState(false);
  const [cameraTarget, setCameraTarget] = useState(null); // 'before' | 'after'

  // Persist minimal state (so if MIUI kills the tab, you restore seamlessly)
  const saveCheckpoint = () => {
    try {
      const chk = {
        selectedPlace: selectedPlace ? { v: selectedPlace.value, l: selectedPlace.label, e: selectedPlace.entity_code } : null,
        selectedTruck: selectedTruck ? { doc: selectedTruck.p_docnumber } : null,
        unloadingMethod,
        qcRequired,
        remarks
      };
      sessionStorage.setItem("UnloadChk", JSON.stringify(chk));
    } catch { }
  };

  const resetTruckState = () => {
    // core form
    setUnloadingMethod('Both');
    setRemarks('');

    // QC
    setQcRequired('N');
    setQcLocked(false);
    setQcTestCode('');
    setQcTestValue('');
    setQcDedAmt('');
    setQcTestStatus('0');

    // existing DB data
    setExisting({
      before_url: null,
      after_url: null,
      before_time: null,
      after_time: null,
      qc_required: null,
      qc_test_code: null,
      qc_test_value: null,
      qc_ded_amt: null
    });

    // photos
    if (beforePreview) URL.revokeObjectURL(beforePreview);
    if (afterPreview) URL.revokeObjectURL(afterPreview);

    setBeforePhoto(null);
    setBeforePreview(null);
    setAfterPhoto(null);
    setAfterPreview(null);

    // photo meta
    setPhotoMeta({
      before: { size_bytes: null, sha256: null },
      after: { size_bytes: null, sha256: null },
    });
  };

  const loadCheckpoint = () => {
    try {
      const s = sessionStorage.getItem("UnloadChk");
      if (!s) return;
      const chk = JSON.parse(s);
      if (chk?.selectedPlace && placeOptions.length) {
        const p = placeOptions.find(o => String(o.value) === String(chk.selectedPlace.v));
        if (p) setSelectedPlace(p);
      }
      if (chk?.selectedTruck && truckList.length) {
        const t = truckList.find(t => String(t.p_docnumber) === String(chk.selectedTruck.doc));
        if (t) setSelectedTruck(t);
      }
      if (chk?.unloadingMethod) setUnloadingMethod(chk.unloadingMethod);
      if (chk?.qcRequired) setQcRequired(chk.qcRequired);
      if (typeof chk?.remarks === "string") setRemarks(chk.remarks);
    } catch { }
  };

  const selectStyles = useMemo(() => ({
    control: (base, state) => ({
      ...base,
      backgroundColor: selectionSource === 'auto' ? '#f0fdf4' : '#fff', // light green when auto
      borderColor: selectionSource === 'auto' ? '#22c55e' : base.borderColor, // green border when auto
      boxShadow: 'none',
      minHeight: 44,
      borderRadius: 10,
    }),
    singleValue: (base) => ({
      ...base,
      fontWeight: selectionSource === 'auto' ? 600 : base.fontWeight,
      backgroundColor: selectionSource === 'auto' ? '#dcfce7' : 'transparent',
      padding: selectionSource === 'auto' ? '2px 6px' : undefined,
      borderRadius: selectionSource === 'auto' ? 6 : undefined,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 30,
      width: '100%',
      borderRadius: 10,
      overflow: 'hidden',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      fontSize: '0.92rem',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      backgroundColor: state.isSelected
        ? '#0b4f8a'
        : state.isFocused
          ? '#e8f3fb'
          : '#fff',
      color: state.isSelected ? '#fff' : '#0f172a',
    }),
  }), [selectionSource]);

  const qcStatusOptions = useMemo(
    () => [
      { value: 'P', label: 'Pending' },
      { value: 'A', label: 'Accepted' },
      { value: 'D', label: 'Accepted with deduction' },
      { value: 'R', label: 'Rejected' },
    ],
    []
  );

  const qcStatusSelectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 44,
        borderRadius: 10,
        borderColor: state.isFocused ? '#38bdf8' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(14,165,233,0.15)' : 'none',
        width: '100%',
      }),
      container: (base) => ({
        ...base,
        width: '100%',
        maxWidth: '100%',
      }),
      menu: (base) => ({
        ...base,
        zIndex: 40,
        width: '100%',
        borderRadius: 10,
        overflow: 'hidden',
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      option: (base, state) => ({
        ...base,
        fontSize: '0.92rem',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        lineHeight: 1.35,
        backgroundColor: state.isSelected
          ? '#0b4f8a'
          : state.isFocused
            ? '#e8f3fb'
            : '#fff',
        color: state.isSelected ? '#fff' : '#0f172a',
      }),
      singleValue: (base) => ({
        ...base,
        maxWidth: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }),
    }),
    []
  );

  // existing photos from DB for selected truck
  const [existing, setExisting] = useState({
    before_url: null,
    after_url: null,
    before_time: null,
    after_time: null,
    qc_required: null,     // ← add this
    qc_test_code: null,
    qc_test_value: null,
    qc_ded_amt: null
  });
  // -------------------------------------------------------------------------------------------------

  // Row.jsx (arrow fn + module classes)
  const Row = ({ icon, title, children, style }) => {
    return (
      <div className={styles.infoRow} style={style} title={title} aria-label={title}>
        <span className={styles.infoIcon} aria-hidden="true">{icon}</span>
        <span className={styles.infoValue}>{children}</span>
      </div>
    );
  }

  const formatKg = (n) => (Number(n)?.toLocaleString?.() ?? n) + " MT";

  /*
  const fetchUnloadingPlaces = async () => {
    const res = await axios.post('/api/wbauto/getUsersUnloadingStations', {
      emp_id,
      entity_code: entityCode,
      token,
    });
    const data = res.data || [];
    const formatted = data.map(row => ({ value: row.L_PlaceId, label: row.L_PlaceName,  }));
    setPlaceOptions(formatted);
  };
  */

  const fetchQcParams = async (docnumber, entity_code) => {
    try {
      setQcLoading(true);

      const res = await axios.post('/api/wbauto/getQcParamsForTruck', {
        entity_code,
        docnumber,
        token
      });

      console.log("QC API RAW RESPONSE:", res);
      console.log("QC API DATA:", res.data);

      const data = res.data || [];

      const formatted = data.map(p => ({
        test_code: p.test_code,
        test_desc: p.test_desc,
        value: p.test_value || ""
      }));

      setQcParams(formatted);

    } catch (err) {
      console.error("QC param load error:", err);
      setQcParams([]);
    } finally {
      setQcLoading(false);
    }
  };

  const fetchUnloadingPlaces = async () => {
    const res = await axios.post('/api/wbauto/getUsersUnloadingStations', {
      emp_id,
      token,
    });
    const data = res.data || [];
    console.log("Stage---0-", data);
    // Build LOV options (all returned rows)
    const formatted = data.map(row => ({
      value: row.L_PlaceId,
      label: row.L_PlaceName,
      entity_code: row.entity_code
    }));
    setPlaceOptions(formatted);

    // Build geofence list (only rows that have usable lat/lng)
    const geoList = data.map(x => ({
      id: x.id ?? x.L_PlaceId ?? x.code,
      name: x.name ?? x.L_PlaceName ?? x.label,
      entity_code: x.entity_code,
      lat: Number(x.lat),
      lng: Number(x.lng),
      radiusMeters: Number(x.radiusMeters ?? x.radius ?? 120)
    })).filter(x => Number.isFinite(x.lat) && Number.isFinite(x.lng));
    setStations(geoList);
  };

  const fetchUnloadingYardPlaces = async (selectedPlaceVal) => {
    const res = await axios.post('/api/wbauto/getUnloadingStations');
    const data = res.data || [];
    const formatted = data
      .filter(row => row.L_PlaceId !== selectedPlaceVal)
      .map(row => ({
        value: row.L_PlaceId,
        label: row.L_PlaceName,
        entity_code: row.entity_code
      }));
    setYardPlaceOptions(formatted)
    setYardTransferPlace(null)
  };

  const fetchTrucks = async (placeId, entityForPlace) => {
    console.log("fetchTrucks Input: ", placeId, entityForPlace);
    if (!placeId) return;
    try {
      const res = await axios.post('/api/wbauto/getUnloadTrucks', {
        emp_id,
        entity_code: entityForPlace ?? entityCode,
        placeId,
        weightype: weightType,
        token,
      });
      console.log(res.data);
      setTruckList(res.data || []);
      setSelectedTruck(null);
      setUnloadingMethod('Both');
      setRemarks('');
      setExisting({ before_url: null, after_url: null, before_time: null, after_time: null });

      setPhotoMeta({
        before: { size_bytes: null, sha256: null },
        after: { size_bytes: null, sha256: null },
      });

    } catch (err) {
      console.error('getUnloadTrucks error:', err);
      setTruckList([]);
      showPopup(err?.response?.data?.message || 'Failed to load trucks.', 'Error');
    }
  };

  const effectiveEntityCode = selectedTruck?.entity_code || selectedPlace?.entity_code || entityCode;

  useEffect(() => {
    if (selectedPlace) {
      console.log("Stage-1-", selectedPlace.value, selectedPlace.entity_code);
      fetchTrucks(selectedPlace.value, selectedPlace.entity_code); // ✅ correct
      fetchUnloadingYardPlaces(selectedPlace.value)
    } else {
      setTruckList([]);
    }
  }, [selectedPlace]);

  const getTruckOption = (truck) => {
    if (!truck) return null;
    return {
      value: truck.p_docnumber,
      label: `${truck.P_truck} - ${truck.p_item_name}`,
      data: truck,
    };
  };

  // after your first fetches (e.g., after trucks load), try restoring once:
  //useEffect(() => { loadCheckpoint(); /* run once after options/trucks available */ }, [placeOptions.length, truckList.length]);
  const didRestoreRef = useRef(false);

  useEffect(() => {
    if (!didRestoreRef.current && placeOptions.length >= 0) {
      loadCheckpoint();
      didRestoreRef.current = true;
    }
  }, [placeOptions.length]);


  // ---------------- Helpers: image + GPS + geofence + existing photos ----------------
  const getLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
      navigator.geolocation.getCurrentPosition(
        pos => resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy
        }),
        err => reject(err),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });

  const readImageFile = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const fr = new FileReader();
      fr.onload = () => { img.onload = () => resolve({ img, url: fr.result }); img.src = fr.result; };
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

  const compressToJpegBlob = async (file, maxSide = 1280, quality = 0.8) => {
    const { img } = await readImageFile(file);
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    return await new Promise(res => canvas.toBlob(b => res(b), "image/jpeg", quality));
  };

  const sha256OfFile = async (file) => {
    // Secure context available → real SHA-256
    if (window.crypto?.subtle?.digest) {
      const buf = await file.arrayBuffer();
      const hash = await window.crypto.subtle.digest('SHA-256', buf);
      return Array.from(new Uint8Array(hash))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    }

    // ❌ Fallback (HTTP / IP-based access)
    console.warn('SHA-256 not available (non-secure context). Using fallback.');

    // Lightweight fallback: name + size + lastModified hash-like string
    return `fallback-${file.size}-${file.lastModified}`;
  };

  const onPhotoPick = async (e, target) => {
    const f = e.target.files?.[0];
    if (!f) return;

    const blob = await compressToJpegBlob(f);
    const file = new File([blob], `${target}.jpg`, { type: 'image/jpeg' });

    const sha = await sha256OfFile(file);
    setPhotoMeta(prev => ({
      ...prev,
      [target]: { size_bytes: file.size, sha256: sha }
    }));

    if (target === 'before') {
      if (beforePreview) URL.revokeObjectURL(beforePreview);
      setBeforePhoto(file);
      setBeforePreview(URL.createObjectURL(file));
    } else {
      if (afterPreview) URL.revokeObjectURL(afterPreview);
      setAfterPhoto(file);
      setAfterPreview(URL.createObjectURL(file));
    }
  };

  useEffect(() => {
    return () => {
      if (beforePreview) URL.revokeObjectURL(beforePreview);
      if (afterPreview) URL.revokeObjectURL(afterPreview);
    };
  }, [beforePreview, afterPreview]);

  // Haversine (meters)
  const haversine = (a, b) => {
    if (!a?.lat || !a?.lng || !b?.lat || !b?.lng) return Infinity;
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };

  const detectStationByGeo = (lat, lng, stationsList) => {
    if (!lat || !lng || !stationsList?.length) return null;
    let best = null;
    for (const st of stationsList) {
      const d = haversine({ lat, lng }, { lat: st.lat, lng: st.lng });
      if (d <= st.radiusMeters && (!best || d < best.dist)) best = { ...st, dist: d };
    }
    return best; // or null
  };

  const fetchExistingPhotos = async (docnumber) => {
    if (!docnumber) return;
    try {
      // Expecting { before_url, after_url, before_time, after_time }
      const res = await axios.post('/api/wbauto/getUnloadTxn', {
        docnumber,
        entity_code: effectiveEntityCode,
        token
      });
      // normalizers
      const ALLOWED = new Set(['Labour', 'Machine', 'Both', 'Self']);
      const pickMethod = (v) => {
        const s = typeof v === 'string' ? v.trim() : '';
        return ALLOWED.has(s) ? s : 'Both';
      };
      const pickYN = (v) => (String(v || '').trim().toUpperCase() === 'Y' ? 'Y' : 'N');


      console.log("Data Stage-1a:", res.data);
      const d = res.data || {};
      console.log("fetchphotos:", d);
      setExisting({
        before_url: d.before_url || null,
        after_url: d.after_url || null,
        before_time: d.before_time || null,
        after_time: d.after_time || null,
        unloading_method: d.unloading_method ?? null,
        remarks: d.remarks || null,
        qc_required: d.qc_required ?? null,   // ← keep as 'Y'/'N'/null

        qcTestCode: d.qc_test_code || null,
        qcTestValue: d.qc_test_value || null,
        qcDedAmt: d.qc_ded_amt || null,
        qcTestStatus: d.qc_test_status ?? null
      });

      // 👇 update the actual form controls
      setUnloadingMethod(pickMethod(d.unloading_method));
      setQcRequired(pickYN(d.qc_required));
      setRemarks(d.remarks || '');

      setQcTestCode(d.qc_test_code || null);
      setQcTestValue(d.qc_test_value || null);
      setQcDedAmt(d.qc_ded_amt || null);
      setQcTestStatus(
        (() => {
          const raw = String(d.qc_test_status ?? '').trim().toUpperCase();
          // New codes
          if (['P', 'A', 'D', 'R'].includes(raw)) return raw;
          // Legacy numeric → letter (same sequence)
          if (raw === '0') return 'P';
          if (raw === '1') return 'A';
          if (raw === '2') return 'D';
          if (raw === '3') return 'R';
          return 'P';
        })()
      );

    } catch (e) {
      console.warn("getUnloadEvents failed; continuing without existing photos.");
      setExisting({ before_url: null, after_url: null, before_time: null, after_time: null });
      setUnloadingMethod('Both');  // fall back
      setQcRequired('N');
      setRemarks('');
      setQcTestStatus('0');
    }
  };
  // ----------------------------------------------------------------------------------------------------

  // replace your existing captureGpsNow with this version
  const captureGpsNow = async (mode = 'silent', opts = {}) => {
    const { onlyIfEmpty = false } = opts;   // 👈 new option
    setGeoLoading(true);
    try {
      const g = await getLocation();
      setGps({ ...g, error: null });

      if (Number.isFinite(g.lat) && Number.isFinite(g.lng) && stations.length) {
        const det = detectStationByGeo(g.lat, g.lng, stations);
        setAutoStation(det || null);

        if (mode === 'refresh' && det) {
          // honor only-if-empty: don't override user’s manual choice
          const allowAuto =
            !onlyIfEmpty || (!selectedPlace && selectionSource !== 'manual');

          if (allowAuto) {
            const exists = placeOptions.some(o => o.value === det.id);
            if (!exists) {
              setPlaceOptions(prev => [...prev, { value: det.id, label: det.name, entity_code: det.entity_code }]);
            }
            if (!selectedPlace || selectedPlace.value !== det.id) {
              isAutoSettingPlace.current = true;
              setSelectedPlace({ value: det.id, label: det.name, entity_code: det.entity_code });
              setSelectionSource('auto');
              setTimeout(() => { isAutoSettingPlace.current = false; }, 0);
            } else {
              setSelectionSource('auto'); // just keep the green styling
            }
          }
        }
      }
      return g;
    } catch (err) {
      const e = { lat: null, lng: null, accuracy: null, error: err?.message || 'GPS unavailable' };
      setGps(e);
      return e;
    } finally {
      setGeoLoading(false);
    }
  };

  // GPS capture that ALSO auto-selects geofenced station in LOV and returns coords

  // On mount: load LOV + geofences, then capture GPS once
  useEffect(() => {
    (async () => {
      await fetchUnloadingPlaces();   // sets placeOptions + stations asynchronously
      await captureGpsNow('silent');  // just capture GPS; don't touch LOV
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Auto-select only on first load (when nothing chosen yet and user didn't pick manually)
    if (stations.length > 0 && !selectedPlace && selectionSource !== 'manual') {
      captureGpsNow('refresh', { onlyIfEmpty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stations]);


  // When truck changes: recapture GPS and load existing photos
  useEffect(() => {
    (async () => {
      if (selectedTruck) {
        await fetchExistingPhotos(selectedTruck.p_docnumber);
        await fetchQcParams(selectedTruck.p_docnumber, selectedTruck.entity_code);
      } else {
        setExisting({ before_url: null, after_url: null, before_time: null, after_time: null });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTruck]);

  useEffect(() => {
    (async () => {
      if (!selectedTruck) { setQcLocked(false); return; }

      try {
        const res = await axios.post('/api/wbauto/isQcMandatory', {
          entity_code: effectiveEntityCode,
          docnumber: selectedTruck.p_docnumber,
          token
        });
        const mandatory = !!res.data?.mandatory;
        setQcLocked(mandatory);
        if (mandatory) setQcRequired('Y');   // default to Yes
      } catch (e) {
        console.warn('isQcMandatory failed:', e?.message || e);
        setQcLocked(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTruck, effectiveEntityCode]);

  // ❌ Removed GPS recapture on LOV change to avoid loops

  const handleQcChange = (index, value) => {
    setQcParams(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        value
      };
      return updated;
    });
  };

  const handleSubmit = async () => {
    // --- quick helpers (scoped to this function) ---
    const asStr = (v, maxLen) => {
      if (v === undefined || v === null) return '';
      // unwrap boxed values (e.g., new String())
      if (typeof v === 'object' && v.valueOf && typeof v.valueOf() !== 'object') v = v.valueOf();
      const s = String(v);
      return maxLen ? s.slice(0, maxLen) : s;
    };
    const asNum = (v) => {
      if (v === undefined || v === null || v === '') return null;
      if (typeof v === 'object' && v.valueOf && typeof v.valueOf() !== 'object') v = v.valueOf();
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const asInt = (v) => {
      const n = asNum(v);
      return n === null ? null : Math.trunc(n);
    };

    // --- validations ---
    if (!selectedTruck || !unloadingMethod) {
      showPopup({
        variant: 'warning',
        title: 'Missing info',
        message: 'Please select a truck and unloading method.'
      });
      return;
    }

    if (!selectedPlace && !autoStation) {
      showPopup({
        variant: 'info',
        title: 'Unloading Place',
        message: 'Select an Unloading Place or tap Refresh GPS so we can auto-pick one.'
      });
      return;
    }

    const hasBefore =
      !!beforePhoto || !!existing?.before_url;

    const hasAfter =
      !!afterPhoto || !!existing?.after_url;

    if (!hasBefore && !hasAfter) {
      showPopup({
        variant: 'warning',
        title: 'Photo required',
        message: 'Please capture at least one photo (Before or After).'
      });
      return;
    }

    const ok = window.confirm(`Confirm unloading submission for Truck: ${selectedTruck.P_truck}?`);
    if (!ok) return;

    // --- final GPS snapshot ---
    const finalGeo = await captureGpsNow('silent');

    // --- choose station (manual > auto) ---
    let chosenLocationCode = selectedPlace?.value ?? "";
    let chosenLocationName = selectedPlace?.label ?? "";
    if (finalGeo?.lat && stations.length) {
      const det = detectStationByGeo(finalGeo.lat, finalGeo.lng, stations);
      setAutoStation(det || null);
      if (det && !selectedPlace?.value) {
        chosenLocationCode = det.id;
        chosenLocationName = det.name;
      }
    }

    // --- prefer entity from TRUCK, then STATION, then fallback state 'entityCode' ---
    const stationForSelected =
      stations.find(s => String(s.id) === String(selectedPlace?.value)) || null;

    const effectiveEntityCode =
      selectedTruck?.entity_code ||
      stationForSelected?.entity_code ||
      entityCode || 'SE';

    // --- sanitize to primitives with DB-aligned lengths ---
    const clean = {
      entity_code: asStr(effectiveEntityCode, 10),
      docnumber: asStr(selectedTruck?.p_docnumber, 50),
      truck_no: asStr(selectedTruck?.P_truck, 32),
      locationCode: asInt(chosenLocationCode),             // INT or null
      locationName: asStr(chosenLocationName, 120),
      lat: asNum(finalGeo?.lat) !== null ? asNum(finalGeo.lat).toFixed(6) : '',
      lng: asNum(finalGeo?.lng) !== null ? asNum(finalGeo.lng).toFixed(6) : '',
      accuracy: asInt(finalGeo?.accuracy) ?? '',
      unloading_method: asStr(unloadingMethod, 20),
      qc_required: (qcRequired === 'Y' ? 'Y' : 'N'),
      remarks: asStr(remarks, 1000),
      emp_id: asStr(emp_id, 32),
    };

    // --- front-end guard for requireds (prevents NULL into SP) ---
    if (!clean.entity_code || !clean.docnumber || !clean.truck_no) {
      showPopup({
        variant: 'warning',
        title: 'Validation',
        message: 'Missing entity_code / docnumber / truck_no. Please reselect the truck and try again.'
      });
      return;
    }

    try {
      // --- build FormData from sanitized values only ---
      const fd = new FormData();
      Object.entries({
        locationCode: clean.locationCode,
        locationName: clean.locationName,
        docnumber: clean.docnumber,
        truck_no: clean.truck_no,
        unloading_method: clean.unloading_method,
        qc_required: clean.qc_required,
        remarks: clean.remarks,
        emp_id: clean.emp_id,
        entity_code: clean.entity_code,
        lat: clean.lat,
        lng: clean.lng,
        accuracy: clean.accuracy,
        token, // keep if backend still expects it
        transfer_location_name: yardTransferPlace?.label ?? null,
        transfer_location_code: yardTransferPlace?.value ?? null,
      }).forEach(([k, v]) => fd.append(k, v ?? ''));

      console.log("Stage--1", fd)

      // ---- QC parameters (new txn columns) ----
      //fd.append('qc_test_code', qcTestCode ?? '');
      //fd.append('qc_test_value', qcTestValue ?? '');

      fd.append('qc_details', JSON.stringify(
        qcParams
          .filter(p => p.value !== "" && p.value !== null)
          .map(p => ({
            test_code: p.test_code,
            test_value: p.value
          }))
      ));

      fd.append('qc_ded_amt', qcDedAmt ?? '');
      fd.append('qc_test_status', qcTestStatus ?? 'P');

      if (beforePhoto) {
        fd.append('before_photo', beforePhoto);
        fd.append('before_photo_size_bytes', photoMeta.before.size_bytes ?? '');
        fd.append('before_photo_sha256', photoMeta.before.sha256 ?? '');
      }

      if (afterPhoto) {
        fd.append('after_photo', afterPhoto);
        fd.append('after_photo_size_bytes', photoMeta.after.size_bytes ?? '');
        fd.append('after_photo_sha256', photoMeta.after.sha256 ?? '');
      }

      console.log("Stage--1", fd)

      const res = await axios.post('/api/wbauto/updateUnloadingTxnQCScreen', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data?.success) {
        setBeforePhoto(null);
        setBeforePreview(null);
        setAfterPhoto(null);
        setAfterPreview(null);

        showPopup({
          variant: 'success',
          title: 'Saved',
          message: `Unloading submission saved.`,
          autocloseMs: 1500
        });

        // refresh list + existing photos
        //fetchTrucks(selectedPlace?.value);
        //await fetchExistingPhotos(selectedTruck.p_docnumber);

        // Refresh trucks for the same place & entity; don't read stale selectedTruck
        if (selectedPlace) {
          await fetchTrucks(selectedPlace.value, selectedPlace.entity_code);
        } else {
          setTruckList([]);
        }
      } else {
        const msg = res.data?.message || 'Failed to update status.';
        showPopup({ variant: 'error', title: 'Error', message: msg });
      }
    } catch (error) {
      console.error('Submit error:', error);
      const msg = error?.response?.data?.message || 'Error submitting data.';
      showPopup({ variant: 'error', title: 'Error', message: msg });
    }
  };

  if (!token) {
    return (
      <div>
        <p>You must be logged in to register a employee.</p>
        <button onClick={() => navigate("/")}>Go to Login</button>
      </div>
    );
  }

  return (
    <div className={styles.approveContainer}>
      <div ref={topRef} />
      <h2 className={styles.screenTitle}>Truck Unloading Entry</h2>
      <div className={styles.gpsRow}>
        <div>
          <strong>GPS: </strong>
          {geoLoading ? "Fetching…" :
            gps.lat ? `lat ${gps.lat.toFixed(6)}, lng ${gps.lng.toFixed(6)} (±${Math.round(gps.accuracy || 0)}m)` :
              gps.error ? `Error: ${gps.error}` : 'Awaiting permission...'}
        </div>
        {/* Optional refresh; auto-capture already covers open, truck change, and submit */}
        {/*<button type="button" className={styles.gpsBtn} onClick={() => captureGpsNow('refresh')}  title="Refresh location"> */}
        <button type="button" className={styles.gpsBtn} onClick={() => captureGpsNow('refresh', { onlyIfEmpty: true })} title="Refresh location">
          Refresh
        </button>
      </div>

      {gps.lat && stations.length > 0 && (
        <div className={styles.geoHint}>
          {(() => {
            const det = detectStationByGeo(gps.lat, gps.lng, stations);
            return det ? (
              <span>📍 Auto-selected station: <strong>{det.name}</strong> ({Math.round(det.dist)}m)</span>
            ) : (
              <span>📍 Outside any geofence radius. Please select manually.</span>
            );
          })()}
        </div>
      )}

      <div className={styles.filters}>
        <label>Unloading Place:</label>
        <Select
          options={placeOptions}
          value={selectedPlace}
          onChange={(val) => {
            const same = val?.value === selectedPlace?.value;

            setSelectedPlace(val);
            setSelectionSource('manual');   // <-- manual choice

            if (same) {
              fetchTrucks(val?.value, val?.entity_code);
            } else {
              setTruckList([]);
              setSelectedTruck(null);
              setUnloadingMethod('Both');
              setRemarks('');
            }
          }}

          className={styles.select}
          styles={selectStyles}
        />
      </div>

      <div className={styles.filters}>
        <label>Select Truck:</label>
        <Select
          options={truckList.map(getTruckOption)}
          value={getTruckOption(selectedTruck)}
          onChange={(option) => setSelectedTruck(option?.data || null)}
          className={styles.select}
          isDisabled={truckList.length === 0}
          placeholder={truckList.length === 0 ? 'No trucks available' : 'Select a truck'}
        />
      </div>

      {selectedTruck && (
        <div className="card p-3 mt-3">
          <h6>Truck Details</h6>
          <div className={styles.infoGrid} style={{ "--icon-size": "22px", "--text-size": "22px" }}>
            <Row
              icon="🚚"
              title="Truck No"
              style={{ "--icon-size": "26px", "--text-size": "22px" }}  // bigger Truck row
            >
              {selectedTruck.P_truck}
            </Row>
          </div>
          <div className={styles.infoGrid}>
            <Row icon="🏭 " title="Transporter">{selectedTruck.p_Trans_name}</Row>
            <Row icon="🆔 " title="Gate Entry No">{selectedTruck.p_docnumber}</Row>
            <Row icon="📦 " title="Item">{selectedTruck.p_item_name}</Row>
            <Row icon="⚖️ " title="Weight">{formatKg(selectedTruck.P_CHALLAN_WT)}</Row>
            <Row icon="🧑 " title="Supplier">{selectedTruck.P_PARTY_NAME}</Row>
            <Row icon="📍  " title="Mines">{selectedTruck.P_LOAD_POINT}</Row>
            <Row icon="🧪 " title="Limits">{selectedTruck.test_min_limits}</Row>
            <Row icon="📝 " title="Limits">{selectedTruck.p_pono}</Row>
            <Row icon="💰 " title="Limits">{selectedTruck.p_posorate}</Row>
            <Row icon="Landed Cost " title="Limits">{selectedTruck.deduct_rate}</Row>

          </div>
          <br></br>
          <label>Unloading Method:</label>
          <Select
            className={styles.select}
            value={{ value: unloadingMethod, label: unloadingMethod }}
            options={['Labour', 'Machine', 'Both', 'Self'].map(v => ({ value: v, label: v }))}
            onChange={(opt) => setUnloadingMethod(opt?.value || 'Both')}
            isSearchable={false}
          />

          <label>QC Check Required?</label>
          <div className={styles.unloadingMethods}>
            {['Y', 'N'].map(v => (
              <label key={v} title={qcLocked && v === 'N' ? 'QC is mandatory for this party/category' : ''}>
                <input
                  type="radio"
                  name="qcRequired"
                  value={v}
                  checked={qcRequired === v}
                  onChange={(e) => !qcLocked && setQcRequired(e.target.value)}
                  disabled={qcLocked && v === 'N'}   // lock the N option only
                  required
                />
                {v === 'Y' ? 'Yes' : 'No'}
              </label>
            ))}
          </div>

          <label>QC Remarks:</label>
          <textarea
            className="form-control"
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
          <h5 className={styles.qcTitle}>
            🧪 Quality Check Parameters
          </h5>
          {qcLoading && (
            <div className={styles.qcLoading}>⏳ Fetching QC parameters...</div>
          )}

          {!qcLoading && qcParams.length === 0 && (
            <div className={styles.qcEmpty}>
              No QC parameters configured.
            </div>
          )}

          {!qcLoading && qcParams.map((param, index) => (
            <div key={param.test_code} className={styles.qcCard}>
              <div className={styles.qcHeader}>
                <div className={styles.qcTitleRow}>
                  <span className={styles.qcCode}>{param.test_code}</span>
                  <span className={styles.qcDash}> - </span>
                  <span className={styles.qcDesc}>{param.test_desc}</span>
                </div>
              </div>
              <input
                type="text"
                className={styles.qcInput}
                placeholder="Enter observed value"
                value={param.value}
                onChange={(e) => handleQcChange(index, e.target.value)}
              />
            </div>
          ))}

          <h5>🧪 QC Test Status</h5>
          <div className={styles.qcStatusWrap}>
            <Select
              className={styles.select}
              classNamePrefix="qcStatus"
              styles={qcStatusSelectStyles}
              isSearchable={false}
              options={qcStatusOptions}
              value={qcStatusOptions.find((o) => o.value === String(qcTestStatus)) || qcStatusOptions[0]}
              onChange={(opt) => setQcTestStatus(opt?.value ?? 'P')}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              menuShouldScrollIntoView={false}
            />
          </div>

          <h5>💰 QC Deduction Amount</h5>
          <input
            disabled={qcTestStatus !== 'D'}
            type="number"
            className="form-control"
            value={qcDedAmt}
            onChange={(e) => setQcDedAmt(e.target.value)}
            placeholder="0.00"
          />

          <div style={{ marginTop: 7, marginBottom: 7 }}>
            <h5>Yard Transfer To Place:</h5>
            <Select
              options={yardPlaceOptions}
              value={yardTransferPlace}
              onChange={(val) => {
                setYardTransferPlace(val);
                setSelectionSource('manual');   // <-- manual choice
              }}

              className={styles.select}
              styles={selectStyles}
            />
          </div>

          {/* Photos (DB + new), stamp-sized, and clickable */}
          <h5>📸 Photos</h5>

          <div className={styles.photoGrid}>

            {/* BEFORE PHOTO */}
            <div className={styles.photoCard}>
              <div className={styles.photoLabel}>Before</div>

              {beforePreview ? (
                <img
                  className={styles.photoThumb}
                  src={beforePreview}
                  alt="Before Preview"
                />
              ) : existing.before_url ? (
                <img
                  className={styles.photoThumb}
                  src={existing.before_url}
                  alt="Before DB"
                />
              ) : null}

              {!useInAppCam && (
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => onPhotoPick(e, 'before')}
                />
              )}

              {useInAppCam && (
                <button
                  type="button"
                  onClick={() => {
                    setCameraTarget('before');
                    setShowInAppCam(true);
                  }}
                >
                  Capture Before
                </button>
              )}
            </div>

            {/* AFTER PHOTO */}
            <div className={styles.photoCard}>
              <div className={styles.photoLabel}>After</div>

              {afterPreview ? (
                <img
                  className={styles.photoThumb}
                  src={afterPreview}
                  alt="After Preview"
                />
              ) : existing.after_url ? (
                <img
                  className={styles.photoThumb}
                  src={existing.after_url}
                  alt="After DB"
                />
              ) : null}

              {!useInAppCam && (
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => onPhotoPick(e, 'after')}
                />
              )}

              {useInAppCam && (
                <button
                  type="button"
                  onClick={() => {
                    setCameraTarget('after');
                    setShowInAppCam(true);
                  }}
                >
                  Capture After
                </button>
              )}
            </div>


          </div>

          <br />
          <button className={styles.approveBtn} onClick={handleSubmit}>
            Submit Unloading Status
          </button>
        </div>
      )}

      <button className={styles.cancelBtn} onClick={() => navigate('/dashboard')}>
        Cancel
      </button>

      {showInAppCam && (
        <InAppCamera
          onCapture={async (file) => {
            try {
              const blob = await compressToJpegBlob(file, 1280, 0.8);
              const named = new File([blob], `${cameraTarget}.jpg`, { type: 'image/jpeg' });

              const sha = await sha256OfFile(named);
              setPhotoMeta(prev => ({
                ...prev,
                [cameraTarget]: { size_bytes: named.size, sha256: sha }
              }));

              if (cameraTarget === 'before') {
                if (beforePreview) URL.revokeObjectURL(beforePreview);
                setBeforePhoto(named);
                setBeforePreview(URL.createObjectURL(named));
              } else {
                if (afterPreview) URL.revokeObjectURL(afterPreview);
                setAfterPhoto(named);
                setAfterPreview(URL.createObjectURL(named));
              }
            } finally {
              setShowInAppCam(false);
              setCameraTarget(null);
            }
          }}
          onCancel={() => {
            setShowInAppCam(false);
            setCameraTarget(null);
          }}
        />
      )}

      <FloatingActions topRef={topRef} bottomRef={bottomRef} />
      <div ref={bottomRef} />
    </div>
  );
};

export default NewUnloadingEntryQCScreen;
